import React, { useState, useEffect } from "react";
import { BRANDS } from "../data/products";
import "../styles/productModal.css";

const fmt = (n) => `MK ${n.toLocaleString()}`;

export default function ProductModal({ product, onAddToCart, onClose }) {
  const isApparel = product.type === "apparel";
  const brand     = BRANDS[product.brand];
  const images    = isApparel
    ? product.colors.map((c) => c.image)
    : (product.images || [product.image]);

  const [slideIdx,    setSlideIdxRaw] = useState(0);
  const [selSize,     setSelSize]     = useState(null);
  const [added,       setAdded]       = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);

  const selColor = isApparel ? product.colors[slideIdx] : null;

  const setSlideIdx = (i) => setSlideIdxRaw(Math.max(0, Math.min(i, images.length - 1)));

  const onTouchStart = (e) => setTouchStartX(e.targetTouches[0].clientX);
  const onTouchEnd   = (e) => {
    if (touchStartX === null) return;
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) setSlideIdx(slideIdx + (diff > 0 ? 1 : -1));
    setTouchStartX(null);
  };

  // Lock body scroll + close on Escape
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const handleAdd = (e) => {
    if (isApparel && !selSize) { alert("Please select a size."); return; }
    onAddToCart({
      ...product,
      ...(isApparel && {
        selColor:    selColor.name,
        selColorHex: selColor.hex,
        selImage:    selColor.image,
        selSize,
      }),
    });
    const rect = e.currentTarget.getBoundingClientRect();
    window.dispatchEvent(new CustomEvent("gvs:add-to-cart", {
      detail: { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
    }));
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  return (
    <div className="product-modal-overlay" onClick={onClose}>
      <div className="product-modal" onClick={(e) => e.stopPropagation()}>
        <button className="product-modal__close" onClick={onClose}>✕</button>

        {/* Image carousel */}
        <div
          className="product-modal__img-box"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <img
            key={images[slideIdx]}
            className="product-modal__img"
            src={images[slideIdx]}
            alt={`${product.name}${selColor ? ` in ${selColor.name}` : ""}`}
          />
          {images.length > 1 && (
            <div className="product-modal__dots">
              {images.map((_, i) => (
                <button
                  key={i}
                  className={`product-modal__dot ${i === slideIdx ? "product-modal__dot--active" : ""}`}
                  onClick={() => setSlideIdx(i)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="product-modal__body">
          <div className="product-modal__meta">
            {brand && (
              <span className="product-modal__brand-tag"
                style={{ background: brand.tagBg, color: brand.tagColor }}>
                {brand.name}
              </span>
            )}
            <p className="product-modal__collection">{product.collection}</p>
          </div>
          <h2 className="product-modal__name">{product.name}</h2>

          {product.description && (
            <p style={{
              fontSize: 13,
              color: "#666",
              lineHeight: 1.65,
              margin: "8px 0 14px",
            }}>
              {product.description}
            </p>
          )}

          <div className="product-modal__footer">
            <span className="product-modal__price">{fmt(product.price)}</span>
            <button
              className={`product-modal__add-btn ${added ? "product-modal__add-btn--done" : ""}`}
              onClick={handleAdd}
            >
              {added ? "✓ Added" : "Add to Bag"}
            </button>
          </div>

          {isApparel && (
            <>
              {/* Colour swatches */}
              <div className="product-modal__section-label">
                Select Color{selColor?.name ? <span className="product-modal__section-value"> — {selColor.name}</span> : ""}
              </div>
              <div className="product-modal__swatches">
                {product.colors.map((c, i) => (
                  <button
                    key={c.name}
                    title={c.name}
                    className="product-modal__swatch"
                    style={{
                      background:  c.hex,
                      outline:     slideIdx === i ? `2px solid ${c.hex}` : "none",
                      outlineOffset: 2,
                    }}
                    onClick={() => setSlideIdx(i)}
                  />
                ))}
              </div>

              {/* Size picker */}
              <div className="product-modal__section-label">
                Select Size{selSize ? <span className="product-modal__section-value"> — {selSize}</span> : ""}
              </div>
              <div className="product-modal__sizes">
                {product.sizes.map((sz) => (
                  <button
                    key={sz}
                    className={`product-modal__size-btn ${selSize === sz ? "product-modal__size-btn--active" : ""}`}
                    onClick={() => setSelSize(sz)}
                  >
                    {sz}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
